import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  Typography,
} from '@material-ui/core'
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import Button from '@/components/button'
import { useIndividualDownloadFilesFromDemand } from '@/hooks/mutations/use-individual-download-files-from-demand'
import { useGetAvailableDisplays } from '@/hooks/queries/use-get-available-displays'
import { useGetDiagnosisById } from '@/hooks/queries/use-get-diagnosis-by-id'
import { useGetUserIntegrations } from '@/hooks/queries/use-get-user-integrations'
import { useCreatePulverizationMap } from '@/pages/diagnosis-view/contexts/create-pulverization-map-context'
import { DownloadTypes } from '@/services/get-url-to-download-file'

import { IntegrationModal } from '../integration-modal'
import { useStyles } from './styles'

export const EXPORT_FILES_LABELS = {
  pulverization: 'Pulverização (shapefile)',
  pulverization_splitted: 'Pulverização por talhão (shapefile)',
  pulverization_pdf: 'Mapa de pulverização (PDF)',
  pulverization_splitted_pdf: 'Mapa de pulverização por talhão (PDF)',
  pulverization_spreadsheet: 'Planilha de pulverização (CSV)',
} as const

const downloadFilesSchema = z
  .object({
    pulverization: z.boolean().default(false),
    pulverization_splitted: z.boolean().default(false),
    pulverization_pdf: z.boolean().default(false),
    pulverization_splitted_pdf: z.boolean().default(false),
    pulverization_spreadsheet: z.boolean().default(false),
  })
  .refine((data) => {
    const isAllFalse = Object.values(data).every((value) => !value)
    return !isAllFalse
  })

type DownloadFilesFormData = z.infer<typeof downloadFilesSchema>

export function ExportFilesMenu() {
  const [exportFilesMenuAnchorEl, setExportFilesMenuAnchorEl] =
    useState<null | HTMLElement>(null)

  const classes = useStyles()
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false)

  const { handleSubmit, reset, register, watch } =
    useForm<DownloadFilesFormData>({
      resolver: zodResolver(downloadFilesSchema),
    })

  const {
    culture,
    diagnosisId,
    pulverizationData,
    pulverizationFromDemandQuery,
    processDemandResumeMutation,
  } = useCreatePulverizationMap()

  const diagnosisByIdQuery = useGetDiagnosisById('weed', culture, diagnosisId!)

  const getAvailableDisplayQuery = useGetAvailableDisplays()
  const userIntegrationsQuery = useGetUserIntegrations()

  const useDownload = (fileType: DownloadTypes) =>
    useIndividualDownloadFilesFromDemand(culture, diagnosisId!, fileType)

  const downloadMutations = {
    pulverization: useDownload('pulverization'),
    pulverization_splitted: useDownload('pulverization_splitted'),
    pulverization_pdf: useDownload('pulverization_pdf'),
    pulverization_splitted_pdf: useDownload('pulverization_splitted_pdf'),
    pulverization_spreadsheet: useDownload('pulverization_spreadsheet'),
  }

  const isAnyDownloadLoading =
    Object.values(downloadMutations).some((mutation) => mutation.isLoading) ||
    pulverizationFromDemandQuery.isFetching

  const isPulverizationFileGenerated =
    Boolean(diagnosisByIdQuery.data?.files?.pulverization) ||
    downloadMutations.pulverization.isSuccess

  const integrationsLayers = useMemo(
    () =>
      pulverizationData?.layers
        .filter((layer) =>
          Boolean(
            getAvailableDisplayQuery.data?.find(
              (display) =>
                display.model === layer.display?.model && display.integration,
            ),
          ),
        )
        .map((layer) => layer.composed_name_layers.join('-')),
    [pulverizationData?.layers, getAvailableDisplayQuery.data],
  )

  function onSubmitFilesDownloadForm(data: DownloadFilesFormData) {
    const withIntegration = Boolean(integrationsLayers?.length)
    const isIntegrationActive = userIntegrationsQuery.data?.find(
      (integration) => integration.name === 'john-deere' && integration.active,
    )

    if (withIntegration && isIntegrationActive && !isPulverizationFileGenerated)
      return setIsIntegrationModalOpen(true)

    handleExportFilesMutate(data)
  }

  function handleExportFilesMutateWithIntegration() {
    const data = watch()
    data.pulverization = true
    handleExportFilesMutate(data, {
      pulverization: () => {
        processDemandResumeMutation.reset()
        pulverizationFromDemandQuery.refetch()
      },
    })
  }

  function handleExportFilesMutate(
    data: DownloadFilesFormData,
    callbacks?: Partial<Record<keyof DownloadFilesFormData, () => void>>,
  ) {
    Object.entries(data)
      .filter(([, checked]) => checked)
      .forEach(([fileType], index) => {
        const downloadType = fileType as keyof typeof EXPORT_FILES_LABELS
        downloadMutations[downloadType].mutate(
          { waitInMs: index * 1000 },
          { onSuccess: callbacks?.[downloadType] },
        )
      })
  }

  function handleClose() {
    setExportFilesMenuAnchorEl(null)
    reset()
  }

  return (
    <>
      <Button onClick={(e) => setExportFilesMenuAnchorEl(e.currentTarget)}>
        Exportar arquivos
      </Button>

      <Popover
        open={!!exportFilesMenuAnchorEl}
        onClose={handleClose}
        anchorEl={exportFilesMenuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{ paper: classes.paper }}
        elevation={2}
      >
        <form onSubmit={handleSubmit(onSubmitFilesDownloadForm)}>
          {Object.entries(EXPORT_FILES_LABELS).map(([fileType, label]) => {
            const downloadType = fileType as keyof typeof EXPORT_FILES_LABELS
            const mutation = downloadMutations[downloadType]
            const isFileGenerated = Boolean(
              diagnosisByIdQuery.data?.files?.[downloadType],
            )

            return (
              <FormControlLabel
                key={fileType}
                control={
                  <Checkbox
                    {...register(downloadType)}
                    disabled={mutation.isLoading}
                  />
                }
                label={
                  <Box
                    display='flex'
                    alignItems='center'
                    gridGap='0.8rem'
                    justifyContent='space-between'
                    width='100%'
                  >
                    <Typography>{label}</Typography>
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      minWidth='24px'
                      height='24px'
                    >
                      {mutation.isLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        (mutation.isSuccess || isFileGenerated) && (
                          <CheckCircleOutlineRoundedIcon
                            fontSize='small'
                            aria-label='Arquivo gerado'
                          />
                        )
                      )}
                    </Box>
                  </Box>
                }
              />
            )
          })}

          <Button type='submit' disabled={isAnyDownloadLoading}>
            {isAnyDownloadLoading ? 'Gerando arquivos' : 'Exportar arquivos'}
          </Button>
        </form>

        {isIntegrationModalOpen && (
          <IntegrationModal
            open={isIntegrationModalOpen}
            onClose={() => setIsIntegrationModalOpen(false)}
            layers={integrationsLayers || []}
            onSubmit={handleExportFilesMutateWithIntegration}
          />
        )}
      </Popover>
    </>
  )
}
